import moment, { type Moment } from "moment"
import { ref } from "vue"

import SubscriptionModel, {
  type Subscription,
  createFreeSubscription,
} from "@Modules/subscriptions/Data/Models/SubscriptionModel"
import TeamModel from "./TeamModel.ts"
import { Team } from "./TeamModel.ts"
import { OrganizationData, TeamData } from "./OrgTypes"
import Visitable from "../../../shared/interfaces/visitable.ts"
import useTeamCan, { TeamFeature } from "../../Auth/useTeamCan.ts"
import { ChecksPermissions } from "../../../shared/interfaces/ChecksPermissions.ts"

export interface Organization extends OrganizationData, Visitable<Organization>, ChecksPermissions<TeamFeature> {
    personalTeam: () => Team | null,
    publicTeams: () => Team[],
    defaultTeam: () => Team | null,
    features: Array<TeamFeature>,
    setFeatures: (features?: TeamFeature[]) => Organization
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- query bag mirrors the untyped router visit payload
    setTeamVisit(callback: (team: Team, query?: Record<string, any>) => void): Organization
    teams: Team[]
    subscription: Subscription
    // Materialized orgs always convert created_at with moment() (see below)
    created_at: Moment
}

/**
 * The default behaviour when you call the visit method
 */
const VisitOrganization = (organization: Organization, query = {}) => {
    const theRouter = window.$router;

    console.log('navigating to org', organization);
    /**
     * First check if this org has a default team set and if so we navigate there instead
     */
    const defaultTeam = organization.defaultTeam();

    if(defaultTeam) {
        console.log('visiting default team', defaultTeam);
        defaultTeam.visit(query);

        return;
    }

    console.log('no default team found?', organization);
    theRouter.push({
        name: "org.dashboard",
        query,
        params: {
            org: organization.slug,
        },
    });
};

/**
 * Takes Organization data from the server and converts it into an object with methods that we can use to interact
 * with it.
 *
 * @param OrganizationData data the data from the server for this team
 * @param data
 * @param shouldAddPersonalTeam If true we add a personal team/workspace
 * @param router Used to swap out the default http router for a fake one in test environments
 */
export default function OrganizationModel(
    data: OrganizationData,
    // Options
    {
        shouldAddPersonalTeam = true,
    },
): Organization {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- query bag mirrors the untyped router visit payload
    let visitOverride: ((model: Organization, query: Record<string, any>) => void) = VisitOrganization;

    /**
     * Convert each team/workspace inside of this org into a smart data object.
     */
    const teams: Team[] = (data.teams || []).map((workspace: TeamData) => {
        /**
         * Hard coding features to demonstrate the permissions system.
         * In the future we will remove these and let the server provide them
         */
        workspace.features = [];


        return TeamModel(data.slug, workspace);
    })

    // Add the personal team for this user
    if (shouldAddPersonalTeam) {
        teams.unshift(
            TeamModel(data.slug, {
                "name": "Personal Team",
                "slug": "personal",
                "abbreviation": null,
                "color": "#024B3B",
                "created_at": moment(),
                "domains": {
                    "personal": "cname.tinyurl.com",
                    "shared": [],
                    "owned": [],
                },
                "blacklisted": false,
                "membership": null,
                "features": [
                    "create_tiny_url",
                    "update_tiny_url",
                    "delete_tiny_url",
                    "archive_tiny_url",
                    "change_long_url",
                    "use_custom_alias",
                    "use_api",
                    "use_analytics",
                    "sso",
                    "use_tags",
                    "manage_expiration",
                    "use_bulk",
                    "download_logs",
                    "use_branded_domains",
                ],
                "options": {},
            }),
        );
    }

    /*k
     * Temporary variables for testing feature arrays. In the future we will
     * add these to the site backend so that we don't need to do this.
     */
    const forcedPermissions: TeamFeature[] = [
        // "view_analytics",
        // "view_urls",
        "view_org_audit",
        "view_org_settings",
        "view_team_settings",
        "view_analytics",
        "view_urls"
    ];

    Object.values(forcedPermissions).map((permission) => {
        data.features.push(permission);
    });

    if(data?.owner === true) {
        data.features.push("create_team");
        data.features.push("archive_team");
        data.features.push("create_organization");
        data.features.push("archive_organization");
        data.features.push("update_organization");
        data.features.push("add_org_user");
        data.features.push("manage_org_roles");
        data.features.push("manage_org_domains");
        data.features.push("manage_org_sso");
        data.features.push("manage_subscriptions");
    } else {
      // Temporarily remove `use_api` to org members. The fix is a BE activity that should be
      // fix before calling https://tinyurl-llc.atlassian.net/browse/TEAM-121 done.
      data.features = data.features.filter((feature: string) => feature !== "use_api");
    }

    // Parse subscription data - use first subscription or create a free subscription
    const currentSubscription = data.subscriptions?.[0]
        ? SubscriptionModel(data.subscriptions[0], data.slug)
        : createFreeSubscription(data.slug)

    const that: Organization = {
        ...data,
        created_at: moment(data.created_at),
        subscription: currentSubscription,
        teams,

        /**
         * Returns the personal team for this user.
         */
        personalTeam: (): Team | null => (teams.find((team: Team) => team.slug === "personal") || null),

        /**
         * Returns a list of teams without your personal team
         */
        publicTeams: (): Team[] => teams.filter((team: Team) => team.slug !== "personal"),

        /**
         * Returns the personal team for this user.
         */
        defaultTeam: (): Team | null => {
            if(that.default_team_slug === null) {
                return null;
            }

            return (teams.find((team: Team) => team.slug === that.default_team_slug) || null);
        },

        /**
         * Allows you to set the function that is called when you call the visit method
         */
        setVisit(callback = (model: Organization, query = {}) => ({})): Organization {
            visitOverride = callback;
            return that;
        },

        /**
         * Allows you to mass call the setVisit method on all teams that belong to this organization
         */
        setTeamVisit(callback = (team: Team, query = {}) => ({})): Organization {
            Object.values(that.teams).forEach((team: Team) => team.setVisit(callback));
            return that;
        },

        /**
         * Redirect the browser to the show page for this org
         * @param query optional query string params to add
         */
        visit: function (query = {}) {
            visitOverride(that, query);
        },

        /**
         * Returns true if the current user has permission to perform an action on this organization
         * this is functionally equivalent to using useTeamCan with only this organization
         */
        can: function (name: TeamFeature | TeamFeature[]): boolean {
            return useTeamCan({ org: ref(that) }).can(name);
        },

        /**
         * Allows you to set features on this organization
         * @param features
         */
        setFeatures: (features: TeamFeature[] = []): Organization => {
            that.features = features;
            return that;
        },
    };

    return that;
}
